import solutionsAiStudio from 'assets/images/solutions/ai-studio.mp4';
import solutionsConfigurator from 'assets/images/solutions/configurator.mp4';
import solutionsCustomizer from 'assets/images/solutions/customizer.mp4';
import solutionsTryon from 'assets/images/solutions/tryon.mp4';
import solutionsViewer from 'assets/images/solutions/viewer.mp4';
import React from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { PrimaryButton } from '../../widgets/buttons';
import { ONBOARDING_TOOLS, OnboardingTool } from './toolSuggestion';

// Each solution is shown as its own block with a looping video at the top — the same clips the
// dashboard solutions page uses — so a seller exploring on their own sees what every tool does at a
// glance, the way the marketing site (zakeke.com) presents them.
const TOOL_VIDEO: Record<OnboardingTool['id'], string> = {
    VC: solutionsCustomizer,
    PC: solutionsConfigurator,
    AI: solutionsAiStudio,
    VTO: solutionsTryon,
    DAM: solutionsViewer
};

// The flagship solution carries a corner ribbon rather than a chip.
const MOST_POPULAR_ID: OnboardingTool['id'] = 'VC';

type SolutionChip = { label: string };

// The audiences each solution is for, shown as chips above its description. The chips for Try-on and
// the 3D Asset Hub are a first proposal — adjust the wording as you like.
const TOOL_CHIPS: Record<OnboardingTool['id'], SolutionChip[]> = {
    VC: [{ label: T._('For printers', 'Admin') }, { label: T._('For embroiderers', 'Admin') }, { label: T._('For engravers', 'Admin') }],
    PC: [{ label: T._('For brands', 'Admin') }, { label: T._('For manufacturers', 'Admin') }],
    AI: [{ label: T._('For merchants', 'Admin') }, { label: T._('For marketers', 'Admin') }, { label: T._('For creators', 'Admin') }],
    VTO: [{ label: T._('For eyewear', 'Admin') }, { label: T._('For jewelry', 'Admin') }, { label: T._('For apparel', 'Admin') }],
    DAM: [{ label: T._('For enterprises', 'Admin') }, { label: T._('For agencies', 'Admin') }, { label: T._('For catalogs', 'Admin') }]
};

const Wrapper = styled.div`
    width: 100%;
    max-width: 1600px;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 32px;
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: bold;
    margin: 0 0 8px 0;
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
`;

const Subtitle = styled.p`
    font-size: 15px;
    color: #888;
    margin: 0;
`;

// Flexbox with a fixed card width so all five sit on a single row when there is room, keeping the
// card size unchanged. On narrower screens the row wraps (centred) to fewer cards rather than forcing
// a horizontal scroll; the last, phone-width breakpoint lets a card fill the width.
const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
`;

const Card = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 300px;
    max-width: 100%;
    background: #fff;
    border: 1px solid #dbe2e6;
    border-radius: 12px;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        border-color: #f08035;
    }
`;

// Same pill as the audience chips, only solid and floated tight into the card's top-left corner.
const PopularBadge = styled.span`
    position: absolute;
    top: 0;
    left: 16px;
    z-index: 1;
    transform: translateY(-50%);
    padding: 5px 12px;
    border-radius: 100px;
    background: #f08035;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
`;

const Media = styled.div`
    width: 100%;
    aspect-ratio: 16 / 10;
    background: #f4f6f7;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
`;

const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 20px;
    border-radius: 0 0 12px 12px;
`;

const ToolName = styled.h2`
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 20px 0;
`;

// A dedicated band for the chips between the name and the description. It keeps a fixed minimum height
// (two chip rows) so the band — and therefore the description and CTA below it — line up across every
// card regardless of how many chips a solution has.
const ChipsSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 6px;
    /* content-box so min-height reserves the same chip area on every card regardless of padding/border,
       keeping the divider — and the description and CTA below it — aligned across all cards. */
    box-sizing: content-box;
    min-height: 56px;
    padding-bottom: 14px;
    margin-bottom: 14px;
    border-bottom: 1px solid #eef1f3;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    background: #e8f0fb;
    color: #2f5aa8;
`;

const ToolDescription = styled.p`
    font-size: 14px;
    line-height: 1.6;
    color: #555;
    margin: 0 0 20px 0;
    flex: 1;
`;

const StartButton = styled(PrimaryButton)`
    align-self: flex-start;
`;

type Props = {
    onGoToTool: (tool: OnboardingTool) => void;
};

const OnboardingSolutions = ({ onGoToTool }: Props) => (
    <Wrapper>
        <Header>
            <Title>{T._('Our solutions', 'Admin')}</Title>
            <Subtitle>{T._('Here is what Zakeke can do. Pick the one that fits your products.', 'Admin')}</Subtitle>
        </Header>

        <Grid>
            {ONBOARDING_TOOLS.map((tool) => (
                <Card key={tool.id}>
                    {tool.id === MOST_POPULAR_ID && <PopularBadge>{T._('Most popular', 'Admin')}</PopularBadge>}
                    <Media>
                        <Video src={TOOL_VIDEO[tool.id]} autoPlay loop muted playsInline />
                    </Media>
                    <Body>
                        <ToolName>{tool.name}</ToolName>
                        <ChipsSection>
                            {TOOL_CHIPS[tool.id].map((chip) => (
                                <Chip key={chip.label}>{chip.label}</Chip>
                            ))}
                        </ChipsSection>
                        <ToolDescription>{tool.description}</ToolDescription>
                        <StartButton type='button' onClick={() => onGoToTool(tool)}>
                            {tool.ctaLabel}
                        </StartButton>
                    </Body>
                </Card>
            ))}
        </Grid>
    </Wrapper>
);

export default OnboardingSolutions;
